import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { fetchShotSync, fetchShotSyncTimeline } from '../../../services/api';
import { useTranslation } from '../../../context/LanguageContext';
import { SHOW_DATA_SOURCE } from '../../../config/features';
import './ShotSyncNew.css';

/* ===== 품질·생산 > 샷동기화 =====
 * Shot-Product Sync Engine(AI 서비스)이 공정 샷과 비전 이미지 중 "같은 제품"끼리 짝지은 결과를 보여준다.
 * 비전검사·융합검사 화면이 쓰는 샷↔이미지 연결을 누가·어떻게 만들었는지(지연 L, 시계 보정, 신뢰도)를 드러내는 화면.
 *
 * 데이터:
 *   /machine/shot-sync/{id}?date=&status=&page=&size=   하루 요약·10분 추이·짝 목록 (오늘이면 20초마다 갱신)
 *   /machine/shot-sync/{id}/timeline?minutes=3           최근 3분 샷·이미지 타임라인 (5초마다 갱신)
 * 시각 문자열은 KST(시간대 없음)라 Date 로 바꿔도 같은 기준으로 비교된다.
 * 스타일은 ss- 접두어만 쓴다(다른 서브탭과 충돌 방지). */

const PAGE_SIZE = 12;
const REFRESH_MS = 20000;
const TIMELINE_MS = 5000;
const TIMELINE_MIN = 3;
const TREND_BUCKETS = 18;               // 10분 × 18 = 최근 3시간
const VISION_IMAGE_VERSION = 2;         // VisionInspectionNew 와 같은 캐시 버전

const STATUS_FILTERS = [['', 'all'], ['MATCH', 'match'], ['CHECK', 'check'], ['UNMATCHED', 'unmatched']];

const pad2 = (n) => String(n).padStart(2, '0');
const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
};
const timeOf = (iso) => {
  if (!iso) return '-';
  const m = String(iso).match(/T(\d{2}:\d{2}:\d{2})/);
  return m ? m[1] : String(iso);
};
const ms = (iso) => (iso ? new Date(iso).getTime() : NaN);
const clock = (t) => { const d = new Date(t); return `${pad2(d.getHours())}:${pad2(d.getMinutes())}:${pad2(d.getSeconds())}`; };
const imageSrc = (file) => (file ? `/vision/${file}?v=${VISION_IMAGE_VERSION}` : null);
const fmt = (v, digits = 1) => (v == null || Number.isNaN(Number(v)) ? '—' : Number(v).toFixed(digits));
const signed = (v, digits = 1) => (v == null ? '—' : `${Number(v) > 0 ? '+' : ''}${Number(v).toFixed(digits)}`);

const statusClass = (s) => {
  if (s === 'MATCH') return 'match';
  if (s === 'CHECK') return 'check';
  if (s === 'PENDING') return 'pending';
  return 'none';                        // IMAGE_ONLY · MISMATCH · SHOT_ONLY
};

/* ---------------------------------------------------------------- 실시간 타임라인 */

const TL = { w: 1200, h: 200, left: 70, right: 16, yShot: 58, yImg: 140 };

function Timeline({ data, tr }) {
  if (!data?.from || !data?.to) return <div className="ss-message">{tr('timeline.empty')}</div>;
  const t0 = ms(data.from);
  const t1 = ms(data.to);
  const span = Math.max(1, t1 - t0);
  const plotW = TL.w - TL.left - TL.right;
  const x = (t) => TL.left + ((t - t0) / span) * plotW;
  const inRange = (t) => t >= t0 - 1000 && t <= t1 + 1000;
  const lag = Number(data.lagS || 0) * 1000;
  const tol = Number(data.tolS || 0) * 1000;

  const shots = (data.shots || []).filter((s) => inRange(ms(s.time)));
  const shotByUuid = new Map(shots.map((s) => [s.shotUuid, s]));
  const images = (data.images || []).filter((im) => inRange(ms(im.time)));
  const paired = new Set(images.filter((im) => im.shotUuid).map((im) => im.shotUuid));
  const settled = data.settledUntil ? ms(data.settledUntil) : null;

  const ticks = [];
  const first = Math.ceil(t0 / 30000) * 30000;
  for (let t = first; t <= t1; t += 30000) ticks.push(t);

  return (
    <svg className="ss-tl" viewBox={`0 0 ${TL.w} ${TL.h}`} role="img" aria-label={tr('timeline.title')}>
      {/* 확정 대기 구간 */}
      {settled && settled < t1 && (
        <g>
          <rect x={Math.max(TL.left, x(settled))} y={18} width={Math.max(0, x(t1) - Math.max(TL.left, x(settled)))} height={TL.h - 40} className="ss-tl-pending-area" />
          <line x1={x(settled)} x2={x(settled)} y1={18} y2={TL.h - 22} className="ss-tl-settle" />
          <text x={Math.min(x(settled) + 6, TL.w - 120)} y={30} className="ss-tl-note">{tr('timeline.pending')}</text>
        </g>
      )}
      {/* 각 샷의 허용 창 (샷 + L ± τ) — 이미지 줄 위에 표시 */}
      {shots.map((s) => {
        const c = ms(s.time) + lag;
        return <rect key={`w-${s.shotUuid}`} x={x(c - tol)} y={TL.yImg - 16} width={Math.max(1, x(c + tol) - x(c - tol))} height={32} rx={4} className="ss-tl-window" />;
      })}
      <line x1={TL.left} x2={TL.w - TL.right} y1={TL.yShot} y2={TL.yShot} className="ss-tl-axis" />
      <line x1={TL.left} x2={TL.w - TL.right} y1={TL.yImg} y2={TL.yImg} className="ss-tl-axis" />
      <text x={8} y={TL.yShot + 4} className="ss-tl-label">{tr('timeline.shots')}</text>
      <text x={8} y={TL.yImg + 4} className="ss-tl-label">{tr('timeline.images')}</text>
      {/* 짝 연결선 */}
      {images.map((im) => {
        const s = im.shotUuid && shotByUuid.get(im.shotUuid);
        if (!s) return null;
        return <line key={`l-${im.imageId}`} x1={x(ms(s.time))} y1={TL.yShot + 8} x2={x(ms(im.time))} y2={TL.yImg - 8} className={`ss-tl-link ss-tl-${statusClass(im.status)}`} />;
      })}
      {/* 공정 샷 */}
      {shots.map((s) => (
        <g key={`s-${s.shotUuid}`}>
          <circle cx={x(ms(s.time))} cy={TL.yShot} r={7} className={paired.has(s.shotUuid) ? 'ss-tl-shot' : 'ss-tl-shot ss-tl-shot-empty'} />
          <text x={x(ms(s.time))} y={TL.yShot - 14} className="ss-tl-shotno">#{s.shotCount}</text>
        </g>
      ))}
      {/* 이미지 (보정된 촬영 시각) */}
      {images.map((im) => (
        <rect key={`i-${im.imageId}`} x={x(ms(im.time)) - 7} y={TL.yImg - 7} width={14} height={14} rx={2} className={`ss-tl-img ss-tl-${statusClass(im.status)}`}>
          <title>{`${timeOf(im.capturedAt)} → ${timeOf(im.time)} · ${im.status}`}</title>
        </rect>
      ))}
      {ticks.map((t) => (
        <text key={t} x={x(t)} y={TL.h - 6} className="ss-tl-tick">{clock(t)}</text>
      ))}
    </svg>
  );
}

/* ---------------------------------------------------------------- 10분 추이 */

function Trend({ buckets, tr }) {
  const rows = (buckets || []).slice(-TREND_BUCKETS);
  if (rows.length === 0) return <div className="ss-message">{tr('trend.empty')}</div>;
  const W = 600;
  const H = 150;
  const left = 34;
  const bottom = 20;
  const maxImg = Math.max(1, ...rows.map((b) => b.images));
  const bw = (W - left) / rows.length;
  const lags = rows.map((b) => (b.lagS != null ? Number(b.lagS) : null)).filter((v) => v != null);
  const offs = rows.map((b) => (b.clockOffsetS != null ? Number(b.clockOffsetS) : null)).filter((v) => v != null);
  const range = (vals) => (vals.length ? `${Math.min(...vals).toFixed(1)} ~ ${Math.max(...vals).toFixed(1)}` : '—');

  return (
    <div className="ss-trend">
      <svg viewBox={`0 0 ${W} ${H}`} className="ss-trend-svg" role="img" aria-label={tr('trend.title')}>
        <text x={0} y={12} className="ss-tl-tick ss-left">{maxImg}</text>
        <line x1={left} x2={W} y1={H - bottom} y2={H - bottom} className="ss-tl-axis" />
        {rows.map((b, i) => {
          const h = (H - bottom - 8);
          const seg = (n) => (n / maxImg) * h;
          let y = H - bottom;
          const parts = [['match', b.matched], ['check', b.check], ['none', b.imageOnly]];
          return (
            <g key={b.time}>
              {parts.map(([cls, n]) => {
                const hh = seg(n || 0);
                y -= hh;
                return hh > 0 ? <rect key={cls} x={left + i * bw + 2} y={y} width={Math.max(1, bw - 4)} height={hh} className={`ss-bar ss-tl-${cls}`} /> : null;
              })}
              {(i % 3 === 0) && <text x={left + i * bw + bw / 2} y={H - 5} className="ss-tl-tick">{timeOf(b.time).slice(0, 5)}</text>}
            </g>
          );
        })}
      </svg>
      <div className="ss-trend-foot">
        <span>{tr('trend.lagRange', { v: range(lags) })}</span>
        <span>{tr('trend.clockRange', { v: range(offs) })}</span>
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------- 화면 */

function ShotSyncNew({ equipmentId }) {
  const { t } = useTranslation();
  const tr = useCallback((key, params) => t(`pages.monitoring.machine.shotSync.${key}`, params), [t]);

  const [date, setDate] = useState(todayStr());
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(0);
  const [data, setData] = useState(null);
  const [timeline, setTimeline] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    setDate(todayStr());
    setStatus('');
    setPage(0);
    setSelected(null);
    setTimeline(null);
  }, [equipmentId]);

  const load = useCallback(async (silent = false) => {
    if (!equipmentId) return;
    if (!silent) setIsLoading(true);
    try {
      const res = await fetchShotSync(equipmentId, { date, status, page, size: PAGE_SIZE });
      setData(res || null);
      setError(null);
    } catch (e) {
      console.error('Failed to load shot sync:', e);
      if (!silent) setError(t('pages.monitoring.machine.common.dataLoadFailed'));
    } finally {
      if (!silent) setIsLoading(false);
    }
  }, [equipmentId, date, status, page, t]);

  useEffect(() => { load(false); }, [load]);
  useEffect(() => {
    if (date !== todayStr()) return undefined;
    const id = setInterval(() => load(true), REFRESH_MS);
    return () => clearInterval(id);
  }, [date, load]);

  const station = data?.station;
  /* 실시간 타임라인 — 스테이션이 켜져 있을 때만 */
  useEffect(() => {
    if (!equipmentId || !station?.enabled) return undefined;
    let alive = true;
    const tick = async () => {
      try {
        const res = await fetchShotSyncTimeline(equipmentId, TIMELINE_MIN);
        if (alive) setTimeline(res || null);
      } catch (e) {
        console.error('Failed to load shot sync timeline:', e);
      }
    };
    tick();
    const id = setInterval(tick, TIMELINE_MS);
    return () => { alive = false; clearInterval(id); };
  }, [equipmentId, station?.enabled]);

  useEffect(() => {
    if (!selected) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setSelected(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selected]);

  const summary = data?.summary;
  const engine = data?.engine;
  const items = data?.items || [];
  const totalItems = data?.totalItems || 0;
  const totalPages = Math.max(1, Math.ceil(totalItems / PAGE_SIZE));
  const dateOptions = useMemo(() => {
    const set = new Set(data?.availableDates || []);
    set.add(todayStr());
    set.add(date);
    return Array.from(set).sort((a, b) => (a < b ? 1 : -1));
  }, [data, date]);

  const reasons = Object.entries(data?.unmatchedReasons || {});
  const demoVerdict = station && (station.demoProcess || station.imageSource === 'DEMO');

  if (isLoading && !data) return <div className="ss-root"><div className="ss-message">{t('pages.monitoring.machine.common.loading')}</div></div>;
  if (error) return <div className="ss-root"><div className="ss-message ss-message-error">{error}</div></div>;

  if (!station) {
    return (
      <div className="ss-root">
        <div className="ss-empty">
          <span className="material-symbols-outlined ss-empty-icon">link_off</span>
          <div className="ss-empty-title">{tr('noStation.title')}</div>
          <div className="ss-empty-desc">{tr('noStation.desc')}</div>
          <div className="ss-empty-meta">{tr('noStation.shots', { n: (summary?.totalShots ?? 0).toLocaleString() })}</div>
        </div>
      </div>
    );
  }

  const kpis = [
    { key: 'images', value: (summary?.images ?? 0).toLocaleString(), tone: 'neutral' },
    { key: 'pairRate', value: summary?.pairRate != null ? `${fmt(summary.pairRate)}%` : '—', tone: 'good' },
    { key: 'confidence', value: fmt(summary?.avgConfidence, 2), tone: 'neutral' },
    { key: 'unmatched', value: ((summary?.imageOnly ?? 0) + (summary?.mismatch ?? 0)).toLocaleString(), tone: 'bad' },
    { key: 'clock', value: engine?.clockOffsetS != null ? tr('unit.sec', { v: fmt(engine.clockOffsetS) }) : '—', tone: 'accent', hint: tr('kpiHint.clock') },
    { key: 'lag', value: engine?.lagS != null ? tr('unit.sec', { v: fmt(engine.lagS) }) : '—', tone: 'accent', hint: tr('kpiHint.lag') },
  ];

  return (
    <div className="ss-root">
      {/* 데이터 출처(설정 SHOW_DATA_SOURCE 일 때만)·엔진 상태 */}
      <div className="ss-head">
        {SHOW_DATA_SOURCE && (
        <div className="ss-sources">
          <span className="ss-source-label">{tr('source.label')}</span>
          <span className={`ss-chip ${station.demoProcess ? 'ss-chip-demo' : 'ss-chip-real'}`}>{tr(station.demoProcess ? 'source.processDemo' : 'source.processReal')}</span>
          <span className={`ss-chip ${station.imageSource === 'DEMO' ? 'ss-chip-demo' : 'ss-chip-real'}`}>{tr(station.imageSource === 'DEMO' ? 'source.imageDemo' : 'source.imageReal')}</span>
          {demoVerdict && <span className="ss-chip ss-chip-demo">{tr('source.verdictDemo')}</span>}
          {station.imageSource === 'DEMO' && (() => {
            /* 데모 카메라 모드에서도 현장 IV4 이미지는 계속 수집된다 — IV4 모드로 바꿀 수 있는지 보는 용도 */
            const last = engine?.liveLastReceivedAt;
            if (!last) return <span className="ss-chip ss-chip-muted">{tr('source.liveNone')}</span>;
            const min = Math.max(0, Math.round((Date.now() - ms(last)) / 60000));
            const ago = min < 60 ? tr('ago.min', { n: min }) : tr('ago.hour', { n: Math.floor(min / 60) });
            return (
              <span className={`ss-chip ${min > 10 ? 'ss-chip-stale' : 'ss-chip-real'}`} title={tr('source.liveHint')}>
                {tr('source.liveLast', { t: timeOf(last), ago })}
              </span>
            );
          })()}
        </div>
        )}
        <div className="ss-engine">
          <span className={`ss-dot ${station.enabled ? 'ss-dot-on' : ''}`} />
          <span>{tr(station.enabled ? 'engine.on' : 'engine.off')}</span>
          <span className="ss-muted">· {tr(`mode.${station.mode}`)}</span>
          {engine?.lastRunAt && <span className="ss-muted">· {tr('engine.lastRun', { t: timeOf(engine.lastRunAt) })}</span>}
        </div>
      </div>

      {engine?.warnings?.length > 0 && (
        <div className="ss-warn">
          <span className="material-symbols-outlined">warning</span>
          {engine.warnings.map((w) => <span key={w}>{w}</span>)}
        </div>
      )}

      <div className="ss-kpis">
        {kpis.map((k) => (
          <div key={k.key} className={`ss-kpi ss-kpi-${k.tone}`} title={k.hint || ''}>
            <span className="ss-kpi-label">{tr(`kpi.${k.key}`)}</span>
            <span className="ss-kpi-value">{k.value}</span>
          </div>
        ))}
      </div>

      <div className="ss-middle">
        <div className="ss-panel">
          <div className="ss-panel-head">
            <span className="ss-panel-title">{tr('timeline.title')}</span>
            <span className="ss-panel-sub">{tr('timeline.sub', { lag: fmt(timeline?.lagS), tol: fmt(timeline?.tolS, 0) })}</span>
          </div>
          {station.enabled ? <Timeline data={timeline} tr={tr} /> : <div className="ss-message">{tr('timeline.off')}</div>}
          <div className="ss-legend">
            <span><i className="ss-lg-shot" />{tr('legend.shot')}</span>
            <span><i className="ss-lg-shot ss-lg-empty" />{tr('legend.shotEmpty')}</span>
            <span><i className="ss-lg-img ss-tl-match" />{tr('legend.match')}</span>
            <span><i className="ss-lg-img ss-tl-check" />{tr('legend.check')}</span>
            <span><i className="ss-lg-img ss-tl-none" />{tr('legend.none')}</span>
            <span><i className="ss-lg-img ss-tl-pending" />{tr('legend.pending')}</span>
            <span><i className="ss-lg-window" />{tr('legend.window')}</span>
          </div>
        </div>
        <div className="ss-panel">
          <div className="ss-panel-head">
            <span className="ss-panel-title">{tr('trend.title')}</span>
            <span className="ss-panel-sub">{tr('trend.sub')}</span>
          </div>
          <Trend buckets={data?.trend} tr={tr} />
        </div>
      </div>

      {/* 필터 */}
      <div className="ss-filter-row">
        <span className="ss-filter-label">{tr('date')}</span>
        <div className="ss-select-wrap">
          <select className="ss-select" value={date} onChange={(e) => { setDate(e.target.value); setPage(0); }}>
            {dateOptions.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
          <span className="material-symbols-outlined ss-select-icon">expand_more</span>
        </div>
        <span className="ss-filter-label">{tr('status')}</span>
        <div className="ss-seg">
          {STATUS_FILTERS.map(([v, key]) => (
            <button key={key} type="button" className={`ss-seg-btn ${status === v ? 'active' : ''}`} onClick={() => { setStatus(v); setPage(0); }}>
              {tr(`filters.${key}`)}
            </button>
          ))}
        </div>
        {reasons.length > 0 && (
          <span className="ss-reasons">
            {tr('reasonsLabel')} {reasons.map(([r, n]) => `${tr(`reasons.${r}`)} ${n}`).join(' · ')}
          </span>
        )}
        <span className="ss-result-count">{tr('resultCount', { n: totalItems.toLocaleString() })}</span>
      </div>

      {/* 목록 */}
      {items.length === 0 ? (
        <div className="ss-message">{tr('empty')}</div>
      ) : (
        <div className="ss-table-wrap">
          <table className="ss-table">
            <thead>
              <tr>
                <th>{tr('col.status')}</th>
                <th>{tr('col.shot')}</th>
                <th>{tr('col.shotTime')}</th>
                <th>{tr('col.image')}</th>
                <th>{tr('col.captured')}</th>
                <th className="ss-right">{tr('col.residual')}</th>
                <th>{tr('col.confidence')}</th>
                <th>{tr('col.verdict')}</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it, idx) => {
                const cls = statusClass(it.status);
                return (
                  <tr key={`${it.kind}-${it.imageId ?? it.shotUuid}-${idx}`} onClick={() => setSelected(it)} className={`ss-row-${cls}`}>
                    <td><span className={`ss-badge ss-badge-${cls}`}>{tr(`statuses.${it.status}`)}</span></td>
                    <td className="ss-num">{it.shotCount != null ? `#${it.shotCount}` : <span className="ss-muted">{it.reason ? tr(`reasons.${it.reason}`) : '—'}</span>}</td>
                    <td className="ss-num">{timeOf(it.shotTime)}</td>
                    <td>{imageSrc(it.imageFile) ? <img className="ss-thumb" src={imageSrc(it.imageFile)} alt="" loading="lazy" /> : '—'}</td>
                    <td className="ss-num">{timeOf(it.capturedAt)} <span className="ss-muted">→ {timeOf(it.savedAt)}</span></td>
                    <td className="ss-num ss-right">{it.residualS != null ? tr('unit.sec', { v: signed(it.residualS) }) : '—'}</td>
                    <td>
                      {it.confidence != null ? (
                        <span className="ss-conf">
                          <span className="ss-conf-bar"><span className={`ss-conf-fill ss-tl-${cls}`} style={{ width: `${Math.min(100, Number(it.confidence) * 100)}%` }} /></span>
                          <span className="ss-num">{fmt(it.confidence, 2)}</span>
                        </span>
                      ) : '—'}
                    </td>
                    <td>
                      {it.verdict ? <span className={`ss-verdict ${it.verdict === 'NG' ? 'ss-verdict-ng' : 'ss-verdict-ok'}`}>{tr(it.verdict === 'NG' ? 'verdicts.ng' : 'verdicts.ok')}</span> : <span className="ss-muted">—</span>}
                      {SHOW_DATA_SOURCE && it.verdictSource === 'DEMO' && <span className="ss-demo-tag">{tr('demoTag')}</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {totalItems > PAGE_SIZE && (
        <div className="ss-pagination">
          <button type="button" className="ss-page-nav" disabled={page === 0} onClick={() => setPage(page - 1)}>
            <span className="material-symbols-outlined">chevron_left</span>{tr('prev')}
          </button>
          <span className="ss-page-info">{page + 1} / {totalPages}</span>
          <button type="button" className="ss-page-nav" disabled={page >= totalPages - 1} onClick={() => setPage(page + 1)}>
            {tr('next')}<span className="material-symbols-outlined">chevron_right</span>
          </button>
        </div>
      )}

      <div className="ss-note">{tr('note')}</div>

      {/* 상세 창 */}
      {selected && (
        <div className="ss-modal-backdrop" onClick={() => setSelected(null)} role="presentation">
          <div className="ss-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            <div className="ss-modal-head">
              <span className="ss-modal-title">
                {selected.shotCount != null ? tr('detail.titleShot', { n: selected.shotCount }) : tr('detail.titleImage')}
              </span>
              <button type="button" className="ss-modal-close" onClick={() => setSelected(null)} aria-label={tr('close')}>
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>
            <div className="ss-modal-body">
              <div className="ss-modal-img">
                {imageSrc(selected.imageFile) ? <img src={imageSrc(selected.imageFile)} alt="" /> : <span className="ss-muted">—</span>}
              </div>
              <dl className="ss-modal-info">
                <dt>{tr('col.status')}</dt><dd><span className={`ss-badge ss-badge-${statusClass(selected.status)}`}>{tr(`statuses.${selected.status}`)}</span></dd>
                {selected.reason && (<><dt>{tr('detail.reason')}</dt><dd>{tr(`reasons.${selected.reason}`)}</dd></>)}
                <dt>{tr('col.shotTime')}</dt><dd className="ss-num">{timeOf(selected.shotTime)}</dd>
                <dt>{tr('detail.captured')}</dt><dd className="ss-num">{timeOf(selected.capturedAt)}</dd>
                <dt>{tr('detail.saved')}</dt><dd className="ss-num">{timeOf(selected.savedAt)}</dd>
                <dt>{tr('col.residual')}</dt><dd className="ss-num">{selected.residualS != null ? tr('unit.sec', { v: signed(selected.residualS, 2) }) : '—'}</dd>
                <dt>{tr('col.confidence')}</dt><dd className="ss-num">{fmt(selected.confidence, 3)}</dd>
                <dt>{tr('col.verdict')}</dt>
                <dd>{selected.verdict || '—'}{SHOW_DATA_SOURCE && selected.verdictSource === 'DEMO' && <span className="ss-demo-tag">{tr('demoTag')}</span>}</dd>
              </dl>
            </div>
            <div className="ss-modal-note">{tr('detail.note')}</div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ShotSyncNew;
